import React, { useEffect, useRef, useState } from 'react'
import Box from '@mui/material/Box'
import Dialog from '@mui/material/Dialog'
import IconButton from '@mui/material/IconButton'
import CloseIcon from '@mui/icons-material/Close'
import type { SxProps, Theme } from '@mui/material/styles'
import { publicSections } from '../help/features'
import type { Feature, FeatureSection } from '../help/features'
import useRouter from '../hooks/useRouter'
import DocsLayout, { DOCS, SM } from '../components/help/DocsLayout'
import type { DocsNavItem } from '../components/help/DocsLayout'
import { DocsIcon, sectionIcon } from '../components/help/docsIcons'

/**
 * The public help (System context, 13 Oct 2026): what each part of Platinum
 * is for and how to use it, from the feature registry (help/features), drawn
 * as the Platinum docs on bayesprice.com/docs. /help is the overview, one
 * card per section; /help/<section> is that section, one heading per
 * feature (its key is the anchor). No login and no API call. Shows the
 * shipped text of the 'everyone' entries only (publicSections): no realm,
 * Manager-only or internal section or feature, no customer's notes, no
 * agent notes.
 */

/** A section's opening sentence, which its overview card shows; the section page shows all of `about`. */
function firstSentence(text: string): string {
  return text.split(/(?<=[.!?])\s+(?=[A-Z])/)[0]
}

const helpHref = (sectionKey?: string) => (sectionKey ? `/help/${sectionKey}` : '/help')

const h1Sx: SxProps<Theme> = { fontFamily: DOCS.heading, fontSize: '36px', lineHeight: '40px', fontWeight: 700, color: DOCS.charcoal, m: 0, mb: '16px' }

/** The website's `.prose` rules for an article page. */
const proseSx: SxProps<Theme> = {
  maxWidth: '65ch',
  color: DOCS.charcoal,
  '& h2': {
    fontFamily: DOCS.heading, fontSize: '24px', lineHeight: 1.1, fontWeight: 600, color: DOCS.charcoal,
    mt: '40px', mb: '16px', mx: 0, pb: '8px', borderBottom: `1px solid ${DOCS.hairline}`,
    scrollMarginTop: '88px', outline: 'none',
  },
  '& p': { m: 0, mb: '16px', lineHeight: 1.75 },
  '& ul, & ol': { m: 0, mb: '16px', pl: '24px' },
  '& ul': { listStyleType: 'disc' },
  '& ol': { listStyleType: 'decimal' },
  '& li': { mb: '8px', lineHeight: 1.75 },
  '& strong': { fontWeight: 600, color: DOCS.charcoal },
  // The website's `.prose .lead`, which outranks `.prose p`.
  '& p.docs-lead': { fontSize: '20px', lineHeight: 1.6, color: DOCS.muted },
}

const leadSx = { fontSize: '20px', lineHeight: 1.6, color: DOCS.muted, m: 0, mb: '16px' } as const

/** "Name: what it changes" as the website's "**Name** — description" bullet; a setting without a short name stays as written. */
function SettingItem({ text }: { text: string }) {
  const m = /^([^:]{1,60}):\s+(.+)$/.exec(text)
  if (!m) return <li>{text}</li>
  return <li><strong>{m[1]}</strong> — {m[2]}</li>
}

/**
 * A screenshot from the registry (`image`, under /help/screenshots/), drawn
 * like the website docs' pictures: full column width at most, a hairline
 * border and rounded corners. A click opens it full size in a modal on the
 * same page (Adam, 14 Oct); Escape, the close button or a click outside
 * closes it.
 */
function DocsScreenshot({ src, alt }: { src: string; alt: string }) {
  const [open, setOpen] = useState(false)
  return (
    <>
      <Box
        component="button"
        type="button"
        onClick={() => setOpen(true)}
        aria-label={`Enlarge picture: ${alt}`}
        sx={{
          display: 'block', p: 0, mb: '24px', lineHeight: 0, border: 0, background: 'none',
          borderRadius: '8px', cursor: 'zoom-in', maxWidth: '100%',
          '&:focus-visible': { outline: `2px solid ${DOCS.blue}`, outlineOffset: '2px' },
        }}
      >
        <Box
          component="img"
          src={src}
          alt={alt}
          loading="lazy"
          decoding="async"
          sx={{
            display: 'block', maxWidth: '100%', height: 'auto',
            border: `1px solid ${DOCS.gray200}`, borderRadius: '8px', bgcolor: DOCS.gray50,
          }}
        />
      </Box>
      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        maxWidth={false}
        aria-label={alt}
        slotProps={{ paper: { sx: { m: 2, borderRadius: '12px', bgcolor: '#fff', maxWidth: 'calc(100vw - 32px)' } } }}
      >
        <Box sx={{ position: 'relative', p: 1.5 }}>
          <IconButton
            aria-label="Close picture"
            onClick={() => setOpen(false)}
            size="small"
            sx={{ position: 'absolute', top: 8, right: 8, bgcolor: 'rgba(255,255,255,0.9)', '&:hover': { bgcolor: '#fff' }, boxShadow: 1 }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
          <Box
            component="img"
            src={src}
            alt={alt}
            sx={{ display: 'block', maxWidth: 'calc(100vw - 80px)', maxHeight: 'calc(100vh - 120px)', width: 'auto', height: 'auto', borderRadius: '8px' }}
          />
          <Box component="p" sx={{ m: 0, mt: 1, fontSize: 14, color: DOCS.gray500 }}>{alt}</Box>
        </Box>
      </Dialog>
    </>
  )
}

function FeatureArticle({ feature }: { feature: Feature }) {
  return (
    <>
      <h2 id={feature.key} tabIndex={-1}>{feature.name}</h2>
      <p>{feature.what}</p>
      <p>{feature.how}</p>
      {feature.image && <DocsScreenshot src={feature.image} alt={feature.imageAlt ?? feature.name} />}
      {!!feature.settings?.length && (
        <>
          <p>Settings:</p>
          <ul>{feature.settings.map((s) => <SettingItem key={s} text={s} />)}</ul>
        </>
      )}
      {!!feature.controls?.length && (
        <>
          <p>On screen:</p>
          <Box
            component="ul"
            aria-label={`${feature.name}: on screen`}
            sx={{
              display: 'flex', flexWrap: 'wrap', gap: '8px', listStyle: 'none !important', pl: '0 !important',
              '& li': {
                m: 0, px: '6px', py: '2px', bgcolor: DOCS.gray100, borderRadius: '4px',
                fontSize: '14px', lineHeight: '20px', color: DOCS.charcoal,
              },
            }}
          >
            {feature.controls.map((c) => <li key={c}>{c}</li>)}
          </Box>
        </>
      )}
    </>
  )
}

const cardSx: SxProps<Theme> = {
  display: 'flex', alignItems: 'flex-start', gap: '16px', p: '16px',
  bgcolor: DOCS.gray50, border: `1px solid ${DOCS.gray200}`, borderRadius: '14px',
  textDecoration: 'none', color: DOCS.blue,
  transition: 'all 150ms cubic-bezier(0.4, 0, 0.2, 1)',
  '& .docs-card-title, & .docs-card-arrow': { transition: 'all 150ms cubic-bezier(0.4, 0, 0.2, 1)' },
  '&:hover': { borderColor: DOCS.blue, bgcolor: '#fff' },
  '&:hover .docs-card-title': { color: DOCS.blue },
  '&:hover .docs-card-arrow': { color: DOCS.blue, transform: 'translateX(4px)' },
}

function SectionCard({ section, onOpen }: { section: FeatureSection; onOpen: (e: React.MouseEvent) => void }) {
  return (
    <Box component="a" href={helpHref(section.key)} onClick={onOpen} sx={cardSx} data-testid={`help-card-${section.key}`}>
      <Box sx={{ flexShrink: 0, width: '40px', height: '40px', borderRadius: '10px', bgcolor: DOCS.blueTint, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <DocsIcon name={sectionIcon(section.key)} size={20} />
      </Box>
      <Box sx={{ flexGrow: 1, minWidth: 0 }}>
        <Box component="h2" className="docs-card-title" sx={{ fontFamily: DOCS.heading, fontSize: '20px', lineHeight: 1.1, fontWeight: 600, color: DOCS.charcoal, m: 0, mt: '28px', mb: '12px' }}>
          {section.title}
        </Box>
        <Box component="p" sx={{ fontSize: '14px', lineHeight: 1.75, color: DOCS.muted, m: 0, mt: '4px', mb: '16px' }}>
          {firstSentence(section.about)}
        </Box>
      </Box>
      <Box className="docs-card-arrow" sx={{ flexShrink: 0, mt: '8px', color: DOCS.gray400, display: 'flex' }}>
        <DocsIcon name="arrow-right" size={20} />
      </Box>
    </Box>
  )
}

function PagerLink({ label, section, dir, onOpen }: { label: string; section: FeatureSection; dir: 'prev' | 'next'; onOpen: (e: React.MouseEvent) => void }) {
  return (
    <Box
      component="a"
      href={helpHref(section.key)}
      rel={dir}
      onClick={onOpen}
      sx={{
        display: 'block', p: '16px', bgcolor: DOCS.gray50, border: `1px solid ${DOCS.gray200}`, borderRadius: '14px',
        textDecoration: 'none', textAlign: dir === 'next' ? 'right' : 'left',
        ...(dir === 'next' ? { [SM]: { gridColumn: 2 } } : {}),
        transition: 'all 150ms cubic-bezier(0.4, 0, 0.2, 1)',
        '&:hover': { borderColor: DOCS.blue, bgcolor: '#fff' },
        '&:hover .docs-pager-title': { textDecoration: 'underline' },
      }}
    >
      <Box component="span" sx={{ display: 'block', fontSize: '14px', lineHeight: '20px', color: DOCS.muted, mb: '4px' }}>{label}</Box>
      <Box component="span" className="docs-pager-title" sx={{ display: 'inline-flex', alignItems: 'center', gap: '8px', color: DOCS.blue, fontWeight: 500 }}>
        {dir === 'prev' && <DocsIcon name="arrow-left" />}
        {section.title}
        {dir === 'next' && <DocsIcon name="arrow-right" />}
      </Box>
    </Box>
  )
}

const Help: React.FC = () => {
  const router = useRouter()
  const sections = publicSections()
  const sectionKey = router.params?.section as string | undefined
  const current = sectionKey ? sections.find((s) => s.key === sectionKey) : undefined
  const index = current ? sections.indexOf(current) : -1
  const scrollRef = useRef<HTMLDivElement>(null)
  // The feature anchor last chosen, per section, so the sidebar marks it.
  const [anchor, setAnchor] = useState(() => ({ section: sectionKey, key: decodeURIComponent(window.location.hash.slice(1)) }))
  const activeAnchor = anchor.section === sectionKey ? anchor.key : ''

  // On a new section: go to the anchor in the address, else to the top.
  useEffect(() => {
    const id = decodeURIComponent(window.location.hash.slice(1))
    const target = id ? document.getElementById(id) : null
    if (target) target.scrollIntoView?.({ block: 'start' })
    else if (scrollRef.current) scrollRef.current.scrollTop = 0
  }, [sectionKey])

  // A feature link followed some other way (the address bar, Back) marks its feature too.
  useEffect(() => {
    const onHash = () => setAnchor({ section: sectionKey, key: decodeURIComponent(window.location.hash.slice(1)) })
    window.addEventListener('hashchange', onHash)
    return () => window.removeEventListener('hashchange', onHash)
  }, [sectionKey])

  const openSection = (key?: string) => (e: React.MouseEvent) => {
    if (e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return
    e.preventDefault()
    if (key) router.navigate('help-section', { section: key })
    else router.navigate('help')
  }

  const goToFeature = (key: string) => {
    const target = document.getElementById(key)
    target?.scrollIntoView?.({ block: 'start' })
    target?.focus({ preventScroll: true })
    window.history.replaceState(window.history.state, '', `#${key}`)
    setAnchor({ section: sectionKey, key })
  }

  const nav: DocsNavItem[] = [
    { title: 'Overview', href: helpHref(), active: !sectionKey, onSelect: () => router.navigate('help') },
    ...sections.map((s) => ({
      title: s.title,
      href: helpHref(s.key),
      active: s.key === sectionKey,
      onSelect: () => router.navigate('help-section', { section: s.key }),
      children: s.features.map((f) => ({
        title: f.name,
        href: `${helpHref(s.key)}#${f.key}`,
        active: s.key === sectionKey && f.key === activeAnchor,
        onSelect: () => goToFeature(f.key),
      })),
    })),
  ]

  return (
    <DocsLayout nav={nav} scrollRef={scrollRef}>
      {!sectionKey && (
        <Box component="article" sx={{ maxWidth: '65ch' }}>
          <Box component="h1" sx={h1Sx}>Platinum Documentation</Box>
          <Box component="p" sx={[leadSx, { lineHeight: 1.75 }]}>
            How each part of Platinum works: what it is for and how to use it.
          </Box>
          <Box sx={{ display: 'grid', gap: '16px', mb: '48px' }}>
            {sections.map((s) => <SectionCard key={s.key} section={s} onOpen={openSection(s.key)} />)}
          </Box>
        </Box>
      )}

      {sectionKey && !current && (
        <Box component="article" sx={proseSx}>
          <Box component="h1" sx={h1Sx}>Page not found</Box>
          <Box component="p" className="docs-lead" sx={leadSx}>There is no help section called “{sectionKey}”.</Box>
          <p>
            <Box component="a" href={helpHref()} onClick={openSection()} sx={{ color: DOCS.blue, fontWeight: 500, textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}>
              See every section
            </Box>
          </p>
        </Box>
      )}

      {current && (
        <Box component="article" aria-labelledby="help-section-title" sx={proseSx}>
          <Box component="h1" id="help-section-title" sx={h1Sx}>{current.title}</Box>
          <Box component="p" className="docs-lead" sx={leadSx}>{current.about}</Box>
          {current.image && <DocsScreenshot src={current.image} alt={current.imageAlt ?? current.title} />}
          {!!current.steps?.length && (
            <>
              <h2 id="typical-route" tabIndex={-1}>A typical route</h2>
              <ol>{current.steps.map((st) => <li key={st}>{st}</li>)}</ol>
            </>
          )}
          {current.features.map((f) => <FeatureArticle key={f.key} feature={f} />)}
          <Box component="nav" aria-label="Previous and next section" sx={{ display: 'grid', gap: '16px', mt: '48px', [SM]: { gridTemplateColumns: '1fr 1fr' } }}>
            {index > 0 && <PagerLink label="Previous" section={sections[index - 1]} dir="prev" onOpen={openSection(sections[index - 1].key)} />}
            {index < sections.length - 1 && <PagerLink label="Next" section={sections[index + 1]} dir="next" onOpen={openSection(sections[index + 1].key)} />}
          </Box>
        </Box>
      )}
    </DocsLayout>
  )
}

export default Help
